import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import invoiceStyles from "../../invoices/styles/invoice.module.css";

// ── Agrupar CuentaXPagar por proveedor (equivalente a useCxcFiliales) ─────────
function agruparPorProveedor(items, filtro, search) {
  const map = {};
  for (const item of items) {
    const fac    = item.factura_cxp;
    const key    = fac?.cuenta_proveedor?.id ?? "sin-proveedor";
    const nombre = fac?.cuenta_proveedor?.nombre ?? "Sin proveedor";
    if (!map[key]) {
      map[key] = { id:key, nombre, totalMonto:0, totalPagado:0, totalSaldo:0, nFacturas:0 };
    }
    map[key].totalMonto  += Number(item.monto  || 0);
    map[key].totalPagado += Number(item.pagado || 0);
    map[key].totalSaldo  += Number(item.saldo  || 0);
    map[key].nFacturas++;
  }
  let filas = Object.values(map);

  if (filtro === "debiendo") filas = filas.filter(r => r.totalSaldo > 0);
  if (filtro === "aldia")    filas = filas.filter(r => r.totalSaldo <= 0);

  if (search.trim())
    filas = filas.filter(r => r.nombre.toLowerCase().includes(search.toLowerCase()));

  return filas.sort((a, b) => b.totalSaldo - a.totalSaldo);
}

// ── Card de resumen — theme-aware ─────────────────────────────────────────────
function SummaryCard({ label, value, icon, color, isCurrency = true }) {
  return (
    <div style={{
      background: "var(--bg-card)",
      border: "1px solid var(--border-card)",
      borderRadius: 10,
      padding: "16px 20px",
      display: "flex",
      alignItems: "center",
      gap: 14,
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: 10, flexShrink: 0,
        background: `${color}18`, border: `1px solid ${color}30`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <i className={`fa-solid ${icon}`} style={{ color, fontSize: "1.1rem" }}/>
      </div>
      <div>
        <p style={{
          margin: 0, color: "var(--text-muted)", fontSize: "0.72rem",
          fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px",
        }}>
          {label}
        </p>
        <p style={{ margin: "2px 0 0", color: "var(--text-primary)", fontSize: "1.1rem", fontWeight: 700 }}>
          {isCurrency ? `₡${formatCurrency(value)}` : value}
        </p>
      </div>
    </div>
  );
}

// ── Fila — theme-aware ────────────────────────────────────────────────────────
function CxPRow({ row, t }) {
  const saldo   = row.totalSaldo;
  const alDia   = saldo <= 0;
  const parcial = !alDia && row.totalPagado > 0;

  return (
    <tr>
      <td style={{ fontWeight: 600, color: "var(--text-primary)" }}>{row.nombre}</td>

      <td>{row.nFacturas} {row.nFacturas !== 1 ? t("cxp.facturas") : t("cxp.facturas")}</td>

      <td>
        <div className="monto-typo" style={{ textAlign: "right", fontWeight: 600 }}>
          ₡{formatCurrency(row.totalMonto)}
        </div>
      </td>

      <td>
        <div className="monto-typo" style={{
          textAlign: "right",
          color: row.totalPagado > 0 ? "#4ade80" : "var(--text-muted)",
        }}>
          ₡{formatCurrency(row.totalPagado)}
        </div>
      </td>

      <td>
        <div className="monto-typo" style={{
          textAlign: "right",
          fontWeight: 600,
          color: alDia ? "#4ade80" : "#f87171",
        }}>
          ₡{formatCurrency(Math.abs(saldo))}
          {saldo < 0 && (
            <span style={{ fontSize: "0.72rem", marginLeft: 4 }}>{t("cxp.aFavor")}</span>
          )}
        </div>
      </td>

      <td className="text-center">
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "3px 10px", borderRadius: 20,
          fontSize: "0.74rem", fontWeight: 600,
          background: alDia
            ? "rgba(74,222,128,0.1)"
            : parcial ? "rgba(251,191,36,0.1)" : "rgba(248,113,113,0.1)",
          color: alDia ? "#4ade80" : parcial ? "#fbbf24" : "#f87171",
          border: `1px solid ${alDia
            ? "rgba(74,222,128,0.25)"
            : parcial ? "rgba(251,191,36,0.25)" : "rgba(248,113,113,0.25)"}`,
        }}>
          <i className={`fa-solid ${alDia ? "fa-check" : parcial ? "fa-clock" : "fa-exclamation"}`}
             style={{ fontSize: "0.65rem" }}/>
          {alDia ? t("cxp.alDia") : parcial ? t("common.parcial") : t("common.pendiente")}
        </span>
      </td>
    </tr>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ListCxP() {
  const { t } = useTranslation();
  const [filtro,       setFiltro]       = useState("debiendo");
  const [search,       setSearch]       = useState("");
  const [showFilters,  setShowFilters]  = useState(false);
  const [page,         setPage]         = useState(1);
  const [allItems,     setAllItems]     = useState([]);
  const [loading,      setLoading]      = useState(true);

  const PAGE_SIZE = 12;

  // FILTROS built inside component so they re-render on language change
  const FILTROS = [
    { value: "debiendo", label: t("cxp.filtroConSaldo"), icon: "fa-exclamation-circle" },
    { value: "aldia",    label: t("cxp.filtroAlDia"),    icon: "fa-check-circle"       },
    { value: "todos",    label: t("cxp.filtroTodos"),    icon: "fa-list"               },
  ];

  const fetchData = useCallback(() => {
    setLoading(true);
    apiFetch("/api/finance/cuentaXPagar/?page_size=500")
      .then(r => r.json())
      .then(d => setAllItems(d.results ?? d ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filas = agruparPorProveedor(allItems, filtro, search);

  const totalPages = Math.ceil(filas.length / PAGE_SIZE);
  const filasPage  = filas.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleSearch(val) { setSearch(val); setPage(1); }
  function handleFiltro(val) { setFiltro(val); setPage(1); }

  const totalSaldo = filas.reduce((a, r) => a + r.totalSaldo, 0);
  const totalMonto = filas.reduce((a, r) => a + r.totalMonto, 0);

  return (
    <>
      {/* ── Breadcrumb ── */}
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("cxp.titulo")}</span>
      </span>

      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2"
        style={{ marginBottom: 6 }}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">{t("cxp.gestion")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("cxp.administra")}</p>
        </div>
      </div>

      {/* ── Cards resumen ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 20 }}>
        <SummaryCard
          label={t("cxp.proveedoresConSaldo")}
          value={filas.filter(r => r.totalSaldo > 0).length}
          icon="fa-building-circle-exclamation"
          color="#f97316"
          isCurrency={false}
        />
        <SummaryCard
          label={t("cxp.totalFacturado")}
          value={totalMonto}
          icon="fa-file-invoice-dollar"
          color="#60a5fa"
        />
        <SummaryCard
          label={t("cxp.totalPendiente")}
          value={totalSaldo}
          icon="fa-money-bill-wave"
          color={totalSaldo > 0 ? "#f87171" : "#4ade80"}
        />
      </div>

      {/* ── Tabla principal ── */}
      <div className={invoiceStyles["table-container"]}>

        {/* Barra búsqueda + filtros */}
        <div className={`${invoiceStyles["table-header"]} d-flex justify-content-between`}>
          <div className={`${invoiceStyles["input-header"]} d-flex gap-2`}>
            <i className="fa-solid fa-magnifying-glass"
               style={{ color: loading ? "#f97316" : undefined, transition: "color 0.2s" }}/>
            <input type="text" placeholder={t("cxp.buscarPlaceholder")}
              value={search} onChange={e => handleSearch(e.target.value)}/>
            {search && (
              <button onClick={() => handleSearch("")}
                style={{ background: "none", border: "none", color: "var(--text-muted)",
                  cursor: "pointer", fontSize: "0.8rem" }}>✕</button>
            )}
          </div>
          <div className={`${invoiceStyles["actions-buttons-header"]} d-flex gap-2`}>
            <button
              className={`btn ${invoiceStyles["btn-filter"]} d-flex gap-2 align-items-center`}
              onClick={() => setShowFilters(v => !v)}>
              <i className="fa-solid fa-filter"/>
              {FILTROS.find(f => f.value === filtro)?.label ?? t("common.filtrar")}
            </button>
          </div>
        </div>

        {/* Panel filtros */}
        {showFilters && (
          <div style={{
            padding: "12px 16px",
            borderBottom: "1px solid var(--border-card)",
            display: "flex", gap: 10, flexWrap: "wrap",
          }}>
            {FILTROS.map(f => (
              <button key={f.value}
                onClick={() => { handleFiltro(f.value); setShowFilters(false); }}
                style={{
                  padding: "6px 14px", borderRadius: 8,
                  border: filtro === f.value ? "1px solid #f97316" : "1px solid var(--border-card)",
                  background: filtro === f.value ? "rgba(249,115,22,0.15)" : "transparent",
                  color: filtro === f.value ? "#f97316" : "var(--text-secondary)",
                  fontSize: "0.83rem", fontWeight: filtro === f.value ? 600 : 400,
                  cursor: "pointer", display: "flex", alignItems: "center",
                  gap: 6, transition: "all 0.15s",
                }}>
                <i className={`fa-solid ${f.icon}`} style={{ fontSize: "0.75rem" }}/>
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Tabla */}
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th>{t("cxp.proveedor")}</th>
                <th>{t("sidebar.facturas")}</th>
                <th><div style={{ textAlign: "right" }}>{t("cxp.totalFacturadoCol")}</div></th>
                <th><div style={{ textAlign: "right" }}>{t("cxp.totalAbonadoCol")}</div></th>
                <th><div style={{ textAlign: "right" }}>{t("cxp.saldoAcumulado")}</div></th>
                <th>{t("common.estado")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 32, color: "var(--text-secondary)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin"
                     style={{ marginRight: 8, color: "#f97316" }}/>
                  {t("cxp.cargando")}
                </td></tr>
              ) : filasPage.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                  <i className="fa-solid fa-inbox"
                     style={{ marginRight: 8, fontSize: "1.2rem" }}/>
                  {t("cxp.sinProveedores")}
                </td></tr>
              ) : (
                filasPage.map(row => <CxPRow key={row.id} row={row} t={t}/>)
              )}
            </tbody>
          </table>
        </div>

        {/* Footer paginación */}
        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }}/>
                {t("common.cargando")}
              </span>
            ) : (
              <>
                <span>{filas.length}</span> {t("cxp.proveedores")} · {t("common.pagina")}{" "}
                <span>{page}</span> {t("common.de")} <span>{Math.max(1, totalPages)}</span>
              </>
            )}
          </p>
          <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
            <button disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}>
              {t("common.anterior")}
            </button>
            <button disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}>
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
